import getAllProducts from "./get-all-products.js";
import getAllCollectionProducts from "./get-all-collection-products.js";

class CollectionColorSwatches {
  constructor() {
    this.productCards = document.querySelectorAll(".product-item");
    this.products = [];

    // Shopify Storefront API credentials - replace with your actual values
    this.storefrontAccessToken = window.theme?.storefrontAccessToken || "";
    this.shopDomain = "merzbschwanen.myshopify.com";
    
    // Get the current collection handle from the page
    this.collectionHandle = this.getCurrentCollectionHandle();

    this.loadProducts().then(() => {
      this.init();
      this.setupMutationObserver();
    });
  }

  getCurrentCollectionHandle() {
    // Try to get collection handle from various possible locations
    // 1. Check for canonical URL that contains /collections/
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      const url = canonicalLink.href;
      const collectionMatch = url.match(/\/collections\/([^\/]+)/);
      if (collectionMatch && collectionMatch[1]) {
        return collectionMatch[1];
      }
    }
    
    // 2. Check for collection data in body classes
    const bodyClasses = document.body.className;
    const templateMatch = bodyClasses.match(/template-collection\s+collection--([^\s]+)/);
    if (templateMatch && templateMatch[1]) {
      return templateMatch[1];
    }
    
    // 3. Check for collection JSON in the page
    const collectionDataEl = document.getElementById('CollectionJson');
    if (collectionDataEl) {
      try {
        const collectionData = JSON.parse(collectionDataEl.textContent);
        if (collectionData && collectionData.handle) {
          return collectionData.handle;
        }
      } catch (e) {
        console.warn('Could not parse collection JSON', e);
      }
    }
    
    // 4. Check URL path as last resort
    const pathMatch = window.location.pathname.match(/\/collections\/([^\/]+)/);
    if (pathMatch && pathMatch[1]) {
      return pathMatch[1];
    }
    
    return null;
  }

  async loadProducts() {
    try {
      if (!this.storefrontAccessToken) {
        console.warn(
          "Storefront API access token not provided. Falling back to HTML method."
        );
      } else {
        // If we have a collection handle, use the collection-specific function
        if (this.collectionHandle) {
          console.log(`Loading products for collection: ${this.collectionHandle}`);
          this.products = await getAllCollectionProducts(
            this.storefrontAccessToken,
            this.shopDomain,
            this.collectionHandle
          );
        } else {
          // Fallback to loading all products if no collection handle is found
          console.log('No collection handle found, loading all products');
          this.products = await getAllProducts(
            this.storefrontAccessToken,
            this.shopDomain
          );
        }

        // Map the API data structure to match the format expected by the rest of the class
        this.products = this.products.map((product) => {
          // Extract color patterns from metafields
          let colorPatterns = [];

          // First check if we have the processed colorData from get-all-products.js
          if (product.colorData && product.colorData.metaobjects) {
            // Extract color data from colorData property which contains processed metaobject data
            colorPatterns = product.colorData.metaobjects.map((metaobject) => {
              return {
                color: metaobject.fields.color || "#FFFFFF",
                label: metaobject.fields.label || metaobject.handle,
              };
            });
          }
          // Fallback to direct metafield references if available
          else if (
            product.metafield &&
            product.metafield.references &&
            product.metafield.references.edges
          ) {
            colorPatterns = product.metafield.references.edges.map((edge) => {
              const colorObj = edge.node;
              const colorField = colorObj.fields.find(
                (field) => field.key === "color"
              );
              const labelField = colorObj.fields.find(
                (field) => field.key === "label"
              );

              return {
                color: colorField ? colorField.value : "#FFFFFF",
                label: labelField ? labelField.value : colorObj.handle,
              };
            });
          }
          // Last fallback to old method
          else {
            colorPatterns =
              product.metafields?.find((m) => m.key === "color-pattern")
                ?.value || [];
          }

          return {
            id: product.id,
            title: product.title,
            handle: product.handle,
            url: `/products/${product.handle}`,
            variants: product.variants.edges
              ? product.variants.edges.map(({ node: v }) => ({
                  id: v.id,
                  title: v.title,
                  sku: v.sku,
                  price: v.price.amount,
                  available: v.availableForSale,
                }))
              : product.variants.map((v) => ({
                  id: v.id,
                  title: v.title,
                  sku: v.sku,
                  price: v.price,
                  available: v.availableForSale,
                })),
            color_patterns: colorPatterns.length
              ? colorPatterns
              : [{ color: "#FFFFFF", label: product.title }],
          };
        });
      }
    } catch (error) {
      console.error("Error loading products:", error);
      // Fallback to HTML method if API fails
    }
  }

  init() {
    if (!this.productCards.length) return;

    this.productCards.forEach((card) => {
      this.processProductCard(card);
    });
  }

  processProductCard(card) {
    const productHandle = card.dataset.productHandle;
    if (!productHandle) return;

    const swatchContainer = card.querySelector(".js-card-color-swatches");
    if (!swatchContainer) return;

    this.renderProductSwatches(productHandle, swatchContainer);
  }

  setupMutationObserver() {
    const productGrid = document.querySelector("#CollectionLoop");
    if (!productGrid) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.classList && node.classList.contains("product-item")) {
                this.processProductCard(node);
              } else {
                const productCards = node.querySelectorAll(".product-item");
                if (productCards.length) {
                  productCards.forEach((card) => this.processProductCard(card));
                }
              }
            }
          });
        }
      });
    });

    observer.observe(productGrid, { childList: true, subtree: true });
  }

  renderProductSwatches(productHandle, container) {
    // Filter products for this specific product's variants
    const baseSkuPrefix = this.getBaseSkuPrefix(productHandle, this.products);

    if (!baseSkuPrefix) {
      container.style.display = "none";
      return;
    }

    // Filter products with matching SKU prefix
    const matchingProducts = this.products.filter(
      (p) =>
        p.variants &&
        p.variants.some((v) => v.sku && v.sku.split(".")[0] === baseSkuPrefix)
    );

    this.renderSwatches(matchingProducts, productHandle, container);
  }

  getBaseSkuPrefix(productHandle, products) {
    const product = products.find((p) => p.handle === productHandle);
    if (!product || !product.variants || !product.variants.length) return null;

    // Find first variant with a SKU
    const variantWithSku = product.variants.find((v) => v.sku);
    if (!variantWithSku) return null;

    return variantWithSku.sku.split(".")[0];
  }

  renderSwatches(products, currentHandle, container) {
    if (!products.length) {
      container.style.display = "none";
      return;
    }

    let html = '<div class="card-color-swatches">';

    // Limit display to first 3 swatches if there are more than 3
    const maxSwatchesToShow = 3;
    const totalSwatches = products.length;
    const displayProducts =
      totalSwatches > maxSwatchesToShow
        ? products.slice(0, maxSwatchesToShow)
        : products;

    displayProducts.forEach((product) => {
      if (!product.color_patterns || !product.color_patterns.length) return;

      const isActive = product.handle === currentHandle;
      const activeClass = isActive ? "is-active" : "";


      html += `
        <a href="/products/${product.handle}" 
           class="card-color-swatch ${activeClass}" 
           title="${product.color_patterns[0].label || ""}"
           style="background-color: ${product.color_patterns[0].color}">
        </a>
      `;
    });

    // Add "+x more colors" button if there are more than maxSwatchesToShow
    if (totalSwatches > maxSwatchesToShow) {
      const extraColors = totalSwatches - maxSwatchesToShow;
      // Get the current product's handle so we can link to it
      const currentProduct =
        products.find((p) => p.handle === currentHandle) || products[0];

      html += `
        <a href="/products/${currentProduct.handle}" 
           class="card-color-swatch--more" 
           title="View ${extraColors} more color${extraColors > 1 ? "s" : ""}">
           +${extraColors} more colors
        </a>
      `;
    }

    html += "</div>";
    container.innerHTML = html;
  }
}

// Initialize for collection pages
document.addEventListener("DOMContentLoaded", () => {
  if (document.querySelector(".collection")) {
    new CollectionColorSwatches();
  }
});

// Initialize when the AJAX pagination loads new content
document.addEventListener("product-grid:loaded", () => {
  if (document.querySelector(".collection")) {
    new CollectionColorSwatches();
  }
});

/**
 * Initializes the color swatches functionality
 */
const initColorSwatches = async () => {
  try {
    // Configure these values for your store
    const storefrontAccessToken = ""; // Add your Storefront API Access Token
    const shopDomain = window.location.hostname; // Or set to your specific domain

    // Get the current collection handle if we're on a collection page
    let collectionHandle = null;
    const pathMatch = window.location.pathname.match(/\/collections\/([^\/]+)/);
    if (pathMatch && pathMatch[1]) {
      collectionHandle = pathMatch[1];
    }

    // Get products based on whether we have a collection handle
    let products;
    if (collectionHandle) {
      products = await getAllCollectionProducts(storefrontAccessToken, shopDomain, collectionHandle);
    } else {
      products = await getAllProducts(storefrontAccessToken, shopDomain);
    }

    // Find the products JSON container and parse it
    const productsJsonContainer = document.getElementById(
      "js-matching-sku-products"
    );
    if (!productsJsonContainer) return;

    // Parse the Liquid-generated JSON
    const productsData = JSON.parse(productsJsonContainer.textContent);

    // Enhance each product with color data from the API
    if (productsData && productsData.products && products) {
      productsData.products.forEach((product) => {
        // Find matching product from API
        const apiProduct = products.find(
          (p) => p.id == product.id || p.handle === product.handle
        );

        if (
          apiProduct &&
          apiProduct.colorData &&
          apiProduct.colorData.metaobjects
        ) {
          // Replace the color patterns with the actual API data
          product.color_data = apiProduct.colorData.metaobjects.map(
            (metaobject) => {
              // Extract color and label from metaobject fields
              return {
                id: metaobject.id,
                handle: metaobject.handle,
                color: metaobject.fields.color || "",
                label: metaobject.fields.label || "",
                hexcode: metaobject.fields.hexcode || "",
              };
            }
          );
        }
      });

      // Replace the container with updated data
      productsJsonContainer.textContent = JSON.stringify(productsData);
    }
  } catch (error) {
    console.error("Error initializing color swatches:", error);
  }
};

// Run initialization when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initColorSwatches);
} else {
  initColorSwatches();
}

export default initColorSwatches;
